/**
 * @file TasksPendingMe.jsx
 * @description Report page listing non-closed tasks assigned to the current user.
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../firebase/UserProvider";
import { listTasks, listTaskUsers } from "../../firebase/tasks";
import {
  getPriorityBadgeClass,
  getStatusBadgeClass,
  formatFirestoreDate,
  PRIORITY_WEIGHT,
  STATE_COLORS,
} from "../../utils/taskHelpers";

const filterOptions = ["All", "Open", "In Progress", "On Hold"];

export default function TasksPendingMe() {
  const { user } = useSession();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("taskNumber");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchData = async () => {
      try {
        const [u, t] = await Promise.all([
          listTaskUsers(),
          listTasks({ assignedTo: user.uid, state: ["Open", "In Progress", "On Hold"] }),
        ]);
        setUsers(u);
        setTasks(t);
      } catch (err) {
        setError(err.message || "Failed to load tasks.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  if (!user) return <div className="p-8 text-center font-bold">Please sign in to view this page.</div>;

  const getCreatorName = (uid) => {
    if (!uid) return "Unknown";
    const found = users.find((u) => u.uid === uid);
    return found ? found.name : "Unknown";
  };

  const handleSortToggle = (field) => {
    if (sortBy === field) { setSortOrder(sortOrder === "asc" ? "desc" : "asc"); }
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const renderSortIndicator = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? " ▴" : " ▾";
  };

  const filteredTasks = activeFilter === "All" ? tasks : tasks.filter((t) => t.status === activeFilter);

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "taskNumber") cmp = (a.taskNumber || 0) - (b.taskNumber || 0);
    else if (sortBy === "title") cmp = (a.title || "").localeCompare(b.title || "");
    else if (sortBy === "createdBy") cmp = getCreatorName(a.createdByUid).localeCompare(getCreatorName(b.createdByUid));
    else if (sortBy === "priority") cmp = (PRIORITY_WEIGHT[a.priority] || 0) - (PRIORITY_WEIGHT[b.priority] || 0);
    else if (sortBy === "status") cmp = (a.status || "").localeCompare(b.status || "");
    else if (sortBy === "dueDate") cmp = (a.dueDate ? new Date(a.dueDate).getTime() : 0) - (b.dueDate ? new Date(b.dueDate).getTime() : 0);
    else if (sortBy === "createdAt") {
      const tA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const tB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      cmp = tA - tB;
    }
    return sortOrder === "asc" ? cmp : -cmp;
  });

  return (
    <div className="container-fluid min-h-screen py-8 px-4 md:px-8" style={{ backgroundColor: "var(--theme-color-canvas)" }}>
      {/* Header */}
      <div className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center" style={{ borderBottom: "1px solid var(--theme-color-mute)" }}>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}>Tasks Pending Me</h1>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>Tasks assigned to you that are currently open, in progress, or on hold.</p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link to="/tasks" className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline" style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body)", borderColor: "var(--theme-color-mute)" }}>← Task Inbox</Link>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="rounded-2xl border shadow-sm p-6 mb-8" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
        <label className="block text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--theme-color-body-mid)" }}>Filter by Status</label>
        <div className="flex flex-wrap gap-3">
          {filterOptions.map((option) => {
            const isActive = activeFilter === option;
            const pillStyle = isActive && option !== "All" ? STATE_COLORS[option].active
              : isActive ? { backgroundColor: "var(--theme-button-primary-bg)", color: "var(--theme-button-primary-text)", borderColor: "var(--theme-button-primary-bg)" }
              : { backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body)", borderColor: "var(--theme-color-mute)" };
            return (
              <button key={option} type="button" onClick={() => setActiveFilter(option)} className="px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 cursor-pointer" style={pillStyle}>
                {option}{option !== "All" && ` (${tasks.filter(t => t.status === option).length})`}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="max-w-xl mx-auto mb-8 px-4 py-3.5 rounded-2xl text-sm font-medium shadow-sm" style={{ backgroundColor: "#fff5f5", border: "1px solid #feb2b2", color: "#c53030" }}>{error}</div>}

      {loading ? (
        <div className="rounded-2xl border shadow-sm p-6 animate-pulse" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="h-8 bg-slate-200 rounded mb-4"></div><div className="h-8 bg-slate-200 rounded mb-4"></div><div className="h-8 bg-slate-200 rounded"></div>
        </div>
      ) : sortedTasks.length === 0 ? (
        <div className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body-mid)" }}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>No Pending Tasks</h3>
          <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--theme-color-body)" }}>You have no tasks currently awaiting your action. All clear!</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10" style={{ backgroundColor: "var(--theme-color-canvas-soft)", borderBottom: "1px solid var(--theme-color-mute)" }}>
                  <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
                    <th className="p-4 pl-6 w-28 cursor-pointer select-none" onClick={() => handleSortToggle("taskNumber")}># Task{renderSortIndicator("taskNumber")}</th>
                    <th className="p-4 cursor-pointer select-none" onClick={() => handleSortToggle("title")}>Title{renderSortIndicator("title")}</th>
                    <th className="p-4 cursor-pointer select-none" onClick={() => handleSortToggle("createdBy")}>Raised By{renderSortIndicator("createdBy")}</th>
                    <th className="p-4 w-28 cursor-pointer select-none" onClick={() => handleSortToggle("priority")}>Priority{renderSortIndicator("priority")}</th>
                    <th className="p-4 w-32 cursor-pointer select-none" onClick={() => handleSortToggle("status")}>Status{renderSortIndicator("status")}</th>
                    <th className="p-4 w-36 cursor-pointer select-none" onClick={() => handleSortToggle("dueDate")}>Due Date{renderSortIndicator("dueDate")}</th>
                    <th className="p-4 pr-6 w-36 cursor-pointer select-none" onClick={() => handleSortToggle("createdAt")}>Created{renderSortIndicator("createdAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTasks.map((t) => (
                    <tr key={t.id} className="transition-colors" style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--theme-color-canvas-soft)")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                      <td className="p-4 pl-6 font-extrabold"><Link to={`/tasks/${t.taskNumber}`} className="hover:underline no-underline" style={{ color: "var(--theme-color-primary)" }}>{t.taskNumber}</Link></td>
                      <td className="p-4 font-semibold max-w-sm truncate" style={{ color: "var(--theme-color-ink)" }}>{t.title}</td>
                      <td className="p-4 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>{getCreatorName(t.createdByUid)}</td>
                      <td className="p-4"><span className={getPriorityBadgeClass(t.priority)}>{t.priority || "Low"}</span></td>
                      <td className="p-4"><span className={getStatusBadgeClass(t.status)}>{t.status}</span></td>
                      <td className="p-4 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>{t.dueDate || "—"}</td>
                      <td className="p-4 pr-6 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>{formatFirestoreDate(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {sortedTasks.map((t) => (
              <div key={t.id} className="rounded-2xl border shadow-sm p-5 space-y-3" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
                <div className="flex justify-between items-center">
                  <Link to={`/tasks/${t.taskNumber}`} className="text-xs font-extrabold hover:underline px-2.5 py-1 rounded" style={{ color: "var(--theme-color-primary)", backgroundColor: "var(--theme-color-canvas-soft)" }}>#{t.taskNumber}</Link>
                  <span className={getStatusBadgeClass(t.status)}>{t.status}</span>
                </div>
                <h3 className="text-base font-bold m-0 line-clamp-2" style={{ color: "var(--theme-color-ink)" }}>{t.title}</h3>
                <div className="pt-2 flex justify-between items-center text-xs" style={{ borderTop: "1px solid var(--theme-color-canvas-soft)" }}>
                  <div><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Raised By</span><span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>{getCreatorName(t.createdByUid)}</span></div>
                  <div className="text-right"><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Due Date</span><span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>{t.dueDate || "—"}</span></div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <div><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Priority</span><span className={getPriorityBadgeClass(t.priority)}>{t.priority || "Low"}</span></div>
                  <div className="text-right"><span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Created</span><span className="font-semibold" style={{ color: "var(--theme-color-body)" }}>{formatFirestoreDate(t.createdAt)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
